import type { SupabaseAdapter } from '../interfaces/supabase';
import type { ContactInquiryBase, ContactInquiry, ContactInquiryStatus } from '../interfaces/contact';
import type { ApiResponse } from '../types';
import { contactInquirySchema } from '../schemas/contact';

/**
 * ContactService
 * 
 * Service class that handles contact inquiry operations.
 * Uses the Supabase adapter for database operations.
 */
export class ContactService {
  private static adapter: SupabaseAdapter;
  private static initialized = false;
  private static readonly TABLE_NAME = 'contact_inquiries';

  /**
   * Initialize the service with a SupabaseAdapter
   * @param adapter SupabaseAdapter implementation
   */
  static initialize(adapter: SupabaseAdapter): void {
    this.adapter = adapter;
    this.initialized = true;
  }

  /**
   * Check if the service has been initialized
   */
  private static checkInitialized(): void {
    if (!this.initialized) {
      throw new Error('ContactService must be initialized with a SupabaseAdapter before use');
    }
  }

  /**
   * Submit a new contact inquiry
   * @param data ContactInquiryBase data
   */
  static async submitInquiry(data: ContactInquiryBase): Promise<ApiResponse<ContactInquiry>> {
    this.checkInitialized();

    try {
      // Validate the input data
      const validation = contactInquirySchema.safeParse(data);
      
      if (!validation.success) {
        return {
          data: null,
          error: 'Invalid form data: ' + validation.error.message
        };
      }

      // First check if the adapter has a dedicated method for contact inquiries
      if (typeof this.adapter.submitContactInquiry === 'function') {
        return await this.adapter.submitContactInquiry(data);
      }

      // Fall back to the legacy method using the client directly
      console.log('Using legacy client method for contact submission');
      
      // Format data for insertion
      const formattedData = {
        ...data,
        status: 'new' as const,
        created_at: new Date().toISOString()
      };

      // Insert into database
      const { data: result, error } = await this.adapter.client
        .from(this.TABLE_NAME)
        .insert(formattedData)
        .select()
        .single();

      if (error) {
        console.error('Supabase error submitting contact inquiry:', error);
        return {
          data: null,
          error: `Failed to submit inquiry: ${error.message}`
        };
      }

      return {
        data: result as ContactInquiry,
        error: null
      };
    } catch (error) {
      console.error('Error submitting contact inquiry:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      };
    }
  }

  /**
   * Get all contact inquiries with optional filtering
   * Only available to authenticated users with appropriate permissions
   */
  static async getInquiries(options?: { 
    limit?: number, 
    offset?: number, 
    status?: ContactInquiryStatus 
  }): Promise<ApiResponse<ContactInquiry[]>> {
    this.checkInitialized();

    try {
      // Check if the user is authenticated
      const user = await this.adapter.getUser();
      if (!user) {
        return {
          data: null,
          error: 'Authentication required to access contact inquiries'
        };
      }

      // Set up query
      let query = this.adapter.client
        .from(this.TABLE_NAME)
        .select('*');

      // Apply filtering
      if (options?.status) {
        query = query.eq('status', options.status);
      }

      // Apply pagination
      if (options?.limit) {
        query = query.limit(options.limit);
      }
      
      if (options?.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 10) - 1);
      }

      // Execute query
      const { data: inquiries, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error getting contact inquiries:', error);
        return {
          data: null,
          error: `Failed to fetch inquiries: ${error.message}`
        };
      }

      return {
        data: inquiries as ContactInquiry[],
        error: null
      };
    } catch (error) {
      console.error('Error getting contact inquiries:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      };
    }
  }

  /**
   * Get a single contact inquiry by ID
   * Only available to authenticated users with appropriate permissions
   */
  static async getInquiry(id: number): Promise<ApiResponse<ContactInquiry>> {
    this.checkInitialized();

    try {
      // Check if the user is authenticated
      const user = await this.adapter.getUser();
      if (!user) {
        return {
          data: null,
          error: 'Authentication required to access contact inquiries'
        };
      }

      // Fetch the inquiry
      const { data: inquiry, error } = await this.adapter.client
        .from(this.TABLE_NAME)
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        console.error('Supabase error getting contact inquiry:', error);
        return {
          data: null,
          error: `Failed to fetch inquiry: ${error.message}`
        };
      }

      return {
        data: inquiry as ContactInquiry,
        error: null
      };
    } catch (error) {
      console.error('Error getting contact inquiry:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      };
    }
  }

  /**
   * Update contact inquiry status and optional notes
   * Only available to authenticated users with appropriate permissions
   */
  static async updateInquiryStatus(
    id: number, 
    status: ContactInquiryStatus, 
    notes?: string
  ): Promise<ApiResponse<ContactInquiry>> {
    this.checkInitialized();

    try {
      // Check if the user is authenticated
      const user = await this.adapter.getUser();
      if (!user) {
        return {
          data: null,
          error: 'Authentication required to update contact inquiries'
        };
      }

      // Prepare update data
      const updateData: { status: ContactInquiryStatus; notes?: string } = { status };
      if (notes !== undefined) {
        updateData.notes = notes;
      }

      // Update the inquiry
      const { data: updatedInquiry, error } = await this.adapter.client
        .from(this.TABLE_NAME)
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Supabase error updating contact inquiry:', error);
        return {
          data: null,
          error: `Failed to update inquiry: ${error.message}`
        };
      }

      return {
        data: updatedInquiry as ContactInquiry,
        error: null
      };
    } catch (error) {
      console.error('Error updating contact inquiry:', error);
      return {
        data: null,
        error: error instanceof Error ? error.message : 'An unknown error occurred'
      };
    }
  }
} 